import { ButtonHTMLAttributes, forwardRef, ElementType } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'as'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  as?: ElementType;
  to?: string; // Para usar con Link
}

/**
 * Button - Botón con estilos adaptativos según tema
 * 
 * - Tema USER (data-theme="user"): Estilos NXT48 del hero (blanco/negro)
 * - Tema ADMIN (data-theme="admin"): Estilos estándar admin (indigo/gris)
 * 
 * Los estilos se aplican automáticamente vía CSS según el contexto del padre
 */
const variantClasses: Record<ButtonVariant, string> = {
  primary: cn(
    // Base (admin por defecto)
    'bg-primary-600 text-white shadow-xs hover:bg-primary-500',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
    // Tema user se aplica vía CSS (ver index.css)
    'btn-primary'
  ),
  
  secondary: cn(
    // Base (admin por defecto)
    'bg-white text-secondary-900 shadow-xs border border-secondary-300 hover:bg-secondary-50',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-500',
    // Tema user se aplica vía CSS (ver index.css)
    'btn-secondary'
  ),
  
  danger: cn(
    'bg-danger-600 text-white shadow-xs hover:bg-danger-500',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger-600'
  ),
  
  ghost: cn(
    'bg-transparent text-secondary-700 hover:bg-secondary-100',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-500'
  ),
  
  outline: cn(
    'bg-transparent text-secondary-700 border border-secondary-300 hover:bg-secondary-50',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
  ),
  
  soft: cn(
    'bg-primary-50 text-primary-700 hover:bg-primary-100',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
  ),
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs rounded-md btn-sm',
  md: 'px-3 py-2 text-sm rounded-md btn-md',
  lg: 'px-4 py-2.5 text-sm rounded-md btn-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', isLoading = false, className = '', disabled, children, as, to, ...props }, ref) => {
    // Determinar el componente a renderizar
    const Component = as || (to ? Link : 'button');
    
    // Box shadow para primary en tema user (se aplica vía CSS, pero por si acaso)
    const buttonStyle = variant === 'primary' && size === 'lg'
      ? undefined // Se maneja en CSS
      : undefined;

    const componentProps: any = {
      ref,
      style: buttonStyle,
      className: cn(
        // Base
        'inline-flex items-center justify-center transition-colors',
        'focus-visible:outline',
        // Disabled
        'disabled:opacity-50 disabled:cursor-not-allowed',
        // Variant & Size
        variantClasses[variant],
        sizeClasses[size],
        className
      ),
      ...(to && { to }),
      ...(Component === 'button' && { disabled: disabled || isLoading }),
      ...props,
    };

    return (
      <Component {...componentProps}>
        {isLoading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        {children}
      </Component>
    );
  }
);

Button.displayName = 'Button';
